import { useCallback, useRef, useState } from "react";

import { EntityId } from "@reduxjs/toolkit";

import { DateTime } from "luxon";

import { Mutex, MutexInterface } from "async-mutex";

import { useAppSelector } from "app/hooks";
import { playbackSessionSelectors } from "../store/selectors.session";

import { useCreatePlaybackStreamMutation } from "services/streamingApi/streamingPodManager.api";
import { luxonUtils } from "utils/luxonUtils";

export const usePlaybackStream = (
    sessionId: string,
    recordingId: EntityId,
    recordingStartTime: string,
    deviceId: EntityId,
    playbackStartDateTime: string | undefined,
    maxRecordingStartupTimeInMs: number,
    onError: () => void,
    onTimeout: () => void,
) => {
    // prevent multiple instances for same start date time
    const isInitializedForStartDateTime = useRef<number>(undefined);

    // prevent simultaneously loading same stream
    const mutex = useRef<Mutex>(new Mutex());
    const mutexReleaser = useRef<MutexInterface.Releaser>(undefined);

    // API call to start recording stream
    const [createPlaybackStream, { data: streamUrl, isLoading }] =
        useCreatePlaybackStreamMutation();

    // max load time protection
    const [loadRecordingTimeout, setLoadRecordingTimeout] =
        useState<NodeJS.Timeout>();

    const cancelLoadRecordingTimeout = useCallback(() => {
        clearTimeout(loadRecordingTimeout);
    }, [loadRecordingTimeout]);

    // get user requested playback mode
    const userRequestedPlayMode = useAppSelector(
        playbackSessionSelectors.selectUserRequestedPlayMode,
    );

    const initializeStream = useCallback(async () => {
        mutexReleaser.current = await mutex.current.acquire();

        if (
            userRequestedPlayMode === "resume" ||
            userRequestedPlayMode === "pause"
        ) {
            // user resumed paused playback without manually changing playback start date time
            // release mutex to accept new requests
            if (mutexReleaser.current) {
                mutexReleaser.current();
            }
            return;
        }

        // determine start date time
        const recordingStartDateTime = DateTime.fromISO(recordingStartTime);
        const currentPlaybackStartDateTime = luxonUtils.fromIsoWithFallback(
            playbackStartDateTime,
            DateTime.now(),
        );

        const startDateTime =
            playbackStartDateTime &&
            currentPlaybackStartDateTime > recordingStartDateTime
                ? currentPlaybackStartDateTime
                : recordingStartDateTime;

        if (isInitializedForStartDateTime.current === startDateTime.valueOf()) {
            // already loaded for specified start date time
            // release mutex to accept new requests
            if (mutexReleaser.current) {
                mutexReleaser.current();
            }
            return;
        }

        // save start date time and cancel previous recording instances
        isInitializedForStartDateTime.current = startDateTime.valueOf();
        cancelLoadRecordingTimeout();

        // create the stream
        const request = createPlaybackStream({
            sessionId: sessionId,
            deviceId: deviceId,
            recordingId: recordingId,
            startTime: startDateTime.toISO() as string,
            originalStartTime: recordingStartDateTime.toISO() as string,
        });

        // max load time protection
        setLoadRecordingTimeout(
            setTimeout(() => {
                // abort request to create stream if request is not yet completed
                request?.abort();

                onTimeout();

                if (mutexReleaser.current) {
                    mutexReleaser.current();
                }
            }, maxRecordingStartupTimeInMs),
        );

        // when request failed, mark recording as failed
        try {
            await request;
        } catch {
            onError();
        } finally {
            if (mutexReleaser.current) {
                mutexReleaser.current();
            }
        }
    }, [
        cancelLoadRecordingTimeout,
        createPlaybackStream,
        maxRecordingStartupTimeInMs,
        onError,
        onTimeout,
        playbackStartDateTime,
        deviceId,
        recordingId,
        recordingStartTime,
        sessionId,
        userRequestedPlayMode,
    ]);

    return {
        initializeStream,
        streamUrl,
        isLoading,
        cancelLoadRecordingTimeout,
    };
};
